import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Users, Pencil, X, Check, Plus, Clock, LayoutGrid, Send, History,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normaliseKey = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
const fmtDate = (d) => { try { return format(parseISO(d), 'EEE d MMM'); } catch { return d; } };
const fmtDateTime = (d) => { try { return format(parseISO(d), 'd MMM HH:mm'); } catch { return d; } };

const logChange = (payload) =>
  axios.post(`${API}/admin/event-board/log`, payload).catch(() => {});

/* ============ Inline capacity editor ============ */
function CapacityCell({ day, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(day.quantity || 1);
  const assigned = (day.claimed_by_list || []).length;
  const over = assigned > (day.quantity || 1);

  const save = async () => {
    const q = parseInt(val, 10);
    if (!q || q < 1) { toast.error('Capacity must be at least 1'); return; }
    try {
      await axios.put(`${API}/open-shifts/${day.id}`, { quantity: q });
      await logChange({ day_id: day.id, date: day.date, action: 'capacity', detail: `Capacity changed from ${day.quantity || 1} to ${q}` });
      toast.success(`Capacity for ${fmtDate(day.date)} set to ${q}`);
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update capacity');
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <Input type="number" min="1" value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          autoFocus className="h-6 w-16 text-xs rounded-sm px-1" data-testid={`capacity-input-${day.id}`} />
        <button onClick={save} className="text-green-400 hover:text-green-300" data-testid={`capacity-save-${day.id}`}><Check className="w-3.5 h-3.5" /></button>
        <button onClick={() => setEditing(false)} className="text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
      </span>
    );
  }
  return (
    <button onClick={() => { setVal(day.quantity || 1); setEditing(true); }}
      className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-sm border transition-colors hover:border-primary/60 ${over ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : 'bg-secondary text-foreground border-border'}`}
      title="Click to change capacity" data-testid={`capacity-btn-${day.id}`}>
      <Users className="w-3 h-3" /> {assigned}/{day.quantity || 1}
      {over && <span className="ml-0.5">· Over +{assigned - (day.quantity || 1)}</span>}
      <Pencil className="w-2.5 h-2.5 opacity-50 ml-0.5" />
    </button>
  );
}

/* ============ Inline time editor for one staff row ============ */
function TimeCell({ day, person, row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState(row?.start_time || '');
  const [end, setEnd] = useState(row?.end_time || '');
  if (!row) return <span className="text-xs text-muted-foreground/60 italic">no shift row</span>;

  const save = async () => {
    if (!start || !end) { toast.error('Set both start and end time'); return; }
    try {
      await axios.put(`${API}/shifts/${row.id}?notify=false`, { start_time: start, end_time: end });
      await logChange({
        day_id: day.id, date: day.date, action: 'times_changed',
        detail: `Shift times set to ${start} – ${end}`,
        affected_user_id: person.user_id, affected_user_name: person.user_name,
      });
      toast.success('Times updated (no email sent)');
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update times');
    }
  };

  const clear = async () => {
    try {
      await axios.put(`${API}/shifts/${row.id}?notify=false`, { start_time: '', end_time: '' });
      await logChange({
        day_id: day.id, date: day.date, action: 'times_cleared',
        detail: 'Shift times cleared — to be confirmed',
        affected_user_id: person.user_id, affected_user_name: person.user_name,
      });
      toast.success('Times cleared — back to "To be confirmed" (no email sent)');
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear times');
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="h-6 w-24 text-xs rounded-sm px-1" data-testid={`time-start-${row.id}`} />
        <span className="text-muted-foreground text-xs">–</span>
        <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="h-6 w-24 text-xs rounded-sm px-1" data-testid={`time-end-${row.id}`} />
        <button onClick={save} className="text-green-400 hover:text-green-300" data-testid={`time-save-${row.id}`}><Check className="w-3.5 h-3.5" /></button>
        {(row.start_time || row.end_time) && (
          <button onClick={clear}
            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-400 hover:bg-amber-500/15 transition-colors"
            title='Remove the times — shift goes back to "To be confirmed"'
            data-testid={`time-clear-${row.id}`}>
            Clear → TBC
          </button>
        )}
        <button onClick={() => setEditing(false)} className="text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
      </span>
    );
  }
  const hasTimes = row.start_time && row.end_time;
  return (
    <button onClick={() => { setStart(row.start_time || ''); setEnd(row.end_time || ''); setEditing(true); }}
      className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-sm border border-transparent hover:border-primary/50 transition-colors ${hasTimes ? 'text-foreground' : 'text-amber-400/90 italic'}`}
      title="Click to edit times" data-testid={`time-btn-${row.id}`}>
      <Clock className="w-3 h-3 opacity-60" />
      {hasTimes ? `${row.start_time} – ${row.end_time}` : 'To be confirmed'}
      <Pencil className="w-2.5 h-2.5 opacity-50" />
    </button>
  );
}

/* ============ Replace staff (Phase 2) ============ */
function ReplaceButton({ day, person, onSaved }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');

  const load = async () => {
    setOpen(true);
    setList(null);
    try {
      const res = await axios.get(`${API}/admin/staff/available?date=${day.date}`);
      setList(res.data?.available || []);
    } catch {
      toast.error('Failed to load available staff');
      setOpen(false);
    }
  };

  const doReplace = async (newUser) => {
    try {
      const res = await axios.post(`${API}/open-shifts/${day.id}/replace-staff`, {
        old_user_id: person.user_id,
        new_user_id: newUser.id,
      });
      const t = res.data?.inherited_times
        ? ` — inherits ${res.data.start_time}–${res.data.end_time}`
        : ' — times "To be confirmed"';
      await Promise.all([
        logChange({
          day_id: day.id, date: day.date, action: 'removed',
          detail: `Removed from this day (replaced by ${newUser.name})`,
          affected_user_id: person.user_id, affected_user_name: person.user_name,
        }),
        logChange({
          day_id: day.id, date: day.date, action: 'assigned',
          detail: res.data?.inherited_times
            ? `Assigned to this day (replacing ${person.user_name}), times ${res.data.start_time} – ${res.data.end_time}`
            : `Assigned to this day (replacing ${person.user_name}) — times to be confirmed`,
          affected_user_id: newUser.id, affected_user_name: newUser.name,
        }),
      ]);
      toast.success(`${person.user_name} → ${newUser.name}${t} (no email sent)`);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Replace failed');
    }
  };

  const filtered = (list || []).filter((u) => u.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8);

  return (
    <div className="relative">
      <button onClick={open ? () => setOpen(false) : load}
        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
        title={`Replace ${person.user_name} with an available staff member`}
        data-testid={`replace-btn-${day.id}-${person.user_id}`}>
        Replace
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-[280px] bg-popover border border-border rounded-sm shadow-lg overflow-hidden">
          <div className="p-2 border-b border-border">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              Available on {fmtDate(day.date)} {list ? `(${list.length})` : ''}
            </p>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name..."
              className="h-6 text-xs rounded-sm" autoFocus data-testid={`replace-search-${day.id}-${person.user_id}`} />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {list === null && <p className="text-xs text-muted-foreground p-2">Loading…</p>}
            {list !== null && filtered.length === 0 && (
              <p className="text-xs text-muted-foreground italic p-2">No available staff found</p>
            )}
            {filtered.map((u) => (
              <button key={u.id} onClick={() => doReplace(u)}
                className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-primary/10 flex items-center justify-between"
                data-testid={`replace-option-${day.id}-${u.id}`}>
                <span>{u.name}</span>
                <span className="text-muted-foreground text-[10px] uppercase">{u.role}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============ TBC toggle (Phase 2) ============ */
function TbcToggle({ day, onSaved }) {
  const toggle = async () => {
    try {
      await axios.put(`${API}/open-shifts/${day.id}`, { tbc: !day.tbc });
      const detail = day.tbc ? 'Day confirmed by client (TBC removed)' : 'Day marked as TBC (awaiting client confirmation)';
      const people = day.claimed_by_list || [];
      if (people.length) {
        await Promise.all(people.map((p) => logChange({
          day_id: day.id, date: day.date, action: 'tbc',
          detail, affected_user_id: p.user_id, affected_user_name: p.user_name,
        })));
      } else {
        await logChange({ day_id: day.id, date: day.date, action: 'tbc', detail });
      }
      toast.success(day.tbc
        ? `${fmtDate(day.date)} — TBC removed, day is confirmed`
        : `${fmtDate(day.date)} marked as TBC (awaiting client)`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update TBC');
    }
  };
  return (
    <button onClick={toggle}
      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border transition-colors ${day.tbc
        ? 'bg-amber-500/20 text-amber-400 border-amber-500/50 hover:bg-amber-500/30'
        : 'border-border text-muted-foreground hover:text-amber-400 hover:border-amber-500/50'}`}
      title={day.tbc ? 'Client has not confirmed this day yet — click to mark confirmed' : 'Mark this day as awaiting client confirmation'}
      data-testid={`tbc-btn-${day.id}`}>
      {day.tbc ? '⏳ TBC — awaiting client' : 'Mark TBC'}
    </button>
  );
}

/* ============ Remove with 1-step inline confirm ============ */
function RemoveButton({ day, person, onSaved }) {
  const [arm, setArm] = useState(false);
  useEffect(() => {
    if (!arm) return undefined;
    const t = setTimeout(() => setArm(false), 3500);
    return () => clearTimeout(t);
  }, [arm]);

  const remove = async () => {
    try {
      await axios.post(`${API}/open-shifts/${day.id}/remove-staff?user_id=${person.user_id}&single_day=true`);
      await logChange({
        day_id: day.id, date: day.date, action: 'removed',
        detail: 'Removed from this day — you are no longer scheduled',
        affected_user_id: person.user_id, affected_user_name: person.user_name,
      });
      toast.success(`${person.user_name} removed from ${fmtDate(day.date)}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove');
    }
  };

  if (arm) {
    return (
      <button onClick={remove} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/30"
        data-testid={`remove-confirm-${day.id}-${person.user_id}`}>
        Sure?
      </button>
    );
  }
  return (
    <button onClick={() => setArm(true)} className="text-muted-foreground hover:text-red-400 transition-colors"
      title={`Remove ${person.user_name} from this day`} data-testid={`remove-btn-${day.id}-${person.user_id}`}>
      <X className="w-3.5 h-3.5" />
    </button>
  );
}

/* ============ Add staff (searchable) ============ */
function AddStaffRow({ day, staffPool, onSaved }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const assignedIds = new Set((day.claimed_by_list || []).map((c) => c.user_id));
  const matches = q.trim()
    ? staffPool.filter((u) => !assignedIds.has(u.id) && u.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6)
    : [];

  const add = async (user) => {
    try {
      const res = await axios.post(`${API}/open-shifts/bulk-add-staff?notify=false`, { user_id: user.id, shift_ids: [day.id] });
      if (res.data?.added > 0) {
        await logChange({
          day_id: day.id, date: day.date, action: 'assigned',
          detail: 'Assigned to this day — times to be confirmed',
          affected_user_id: user.id, affected_user_name: user.name,
        });
        toast.success(`${user.name} added to ${fmtDate(day.date)} — times "To be confirmed"`);
      } else {
        toast.warning(`${user.name} was skipped (overlapping shift on this date)`);
      }
      setQ(''); setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add staff');
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1.5">
        <Plus className="w-3.5 h-3.5 text-primary" />
        <Input value={q} placeholder="Add staff — type a name..."
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="h-7 text-xs rounded-sm max-w-[240px]"
          data-testid={`add-staff-input-${day.id}`} />
      </div>
      {open && q.trim() && matches.length === 0 && (
        <div className="absolute z-20 mt-1 w-[260px] bg-popover border border-border rounded-sm shadow-lg p-2">
          <p className="text-xs text-muted-foreground italic" data-testid={`add-staff-nomatch-${day.id}`}>
            No matching active staff — suspended accounts are hidden
          </p>
        </div>
      )}
      {open && matches.length > 0 && (
        <div className="absolute z-20 mt-1 w-[260px] bg-popover border border-border rounded-sm shadow-lg overflow-hidden">
          {matches.map((u) => (
            <button key={u.id} onClick={() => add(u)}
              className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-primary/10 flex items-center justify-between"
              data-testid={`add-staff-option-${day.id}-${u.id}`}>
              <span>{u.name}</span>
              <span className="text-muted-foreground text-[10px] uppercase">{u.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ Apply-to-all-day times ============ */
function DayTimesButton({ day, rows, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  if (!rows.length) return null;

  const apply = async () => {
    if (!start || !end) { toast.error('Set both start and end time'); return; }
    try {
      await Promise.all(rows.map((r) => axios.put(`${API}/shifts/${r.id}?notify=false`, { start_time: start, end_time: end })));
      const nameOf = (empId) => ((day.claimed_by_list || []).find((c) => c.user_id === empId) || {}).user_name || '';
      await Promise.all(rows.map((r) => logChange({
        day_id: day.id, date: day.date, action: 'times_changed',
        detail: `Shift times set to ${start} – ${end}`,
        affected_user_id: r.employee_id, affected_user_name: nameOf(r.employee_id),
      })));
      toast.success(`Times ${start}–${end} applied to ${rows.length} staff on ${fmtDate(day.date)} (no email sent)`);
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply times');
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="h-6 w-24 text-xs rounded-sm px-1" data-testid={`daytime-start-${day.id}`} />
        <span className="text-muted-foreground text-xs">–</span>
        <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="h-6 w-24 text-xs rounded-sm px-1" data-testid={`daytime-end-${day.id}`} />
        <button onClick={apply} className="text-green-400 hover:text-green-300" data-testid={`daytime-save-${day.id}`}><Check className="w-3.5 h-3.5" /></button>
        <button onClick={() => setEditing(false)} className="text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
      </span>
    );
  }
  return (
    <button onClick={() => setEditing(true)}
      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
      data-testid={`daytime-btn-${day.id}`}>
      Set whole day times
    </button>
  );
}

/* ============ Phase 3: smart notify ============ */
function NotifyButton({ days, pendingCount, affectedCount, onDone }) {
  const [sending, setSending] = useState(false);
  const notify = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/event-board/notify`, { day_ids: days.map((d) => d.id) });
      if (res.data?.notified_users > 0) {
        toast.success(`Summary email sent to ${res.data.notified_users} staff member(s) — ${res.data.changes_marked} change(s) covered`);
      } else {
        toast.info(res.data?.message || 'No unnotified changes to send');
      }
      if (res.data?.failed?.length) toast.warning(`Could not email: ${res.data.failed.join(', ')}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to notify staff');
    } finally {
      setSending(false);
    }
  };
  return (
    <Button onClick={notify} disabled={sending || pendingCount === 0} size="sm"
      className={`rounded-sm text-xs ${pendingCount > 0 ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}`}
      variant={pendingCount > 0 ? 'default' : 'outline'}
      title={pendingCount > 0
        ? `Email ${affectedCount} affected staff a summary of ${pendingCount} change(s)`
        : 'No changes since the last notification'}
      data-testid="board-notify-btn">
      <Send className="w-3.5 h-3.5 mr-1.5" />
      {sending ? 'Sending…' : pendingCount > 0 ? `Notify staff (${pendingCount} change${pendingCount === 1 ? '' : 's'} · ${affectedCount} staff)` : 'Notify staff — all up to date'}
    </Button>
  );
}

/* ============ Phase 3: change log panel ============ */
function LogPanel({ entries }) {
  if (!entries.length) {
    return <p className="text-xs text-muted-foreground italic p-3">No changes recorded yet — edits made on this board will appear here.</p>;
  }
  return (
    <div className="max-h-80 overflow-y-auto divide-y divide-border/50" data-testid="board-log-panel">
      {entries.map((e) => (
        <div key={e.id} className="px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid={`board-log-entry-${e.id}`}>
          <span className="text-muted-foreground whitespace-nowrap w-[90px]">{fmtDateTime(e.timestamp)}</span>
          <span className="text-foreground/80 whitespace-nowrap w-[90px]">{e.date ? fmtDate(e.date) : '—'}</span>
          <span className="flex-1 min-w-[200px] text-foreground">
            {e.affected_user_name && <strong className="text-primary">{e.affected_user_name}: </strong>}
            {e.detail || e.action}
          </span>
          <span className="text-muted-foreground whitespace-nowrap">by {e.actor_name}</span>
          {e.affected_user_id ? (
            e.notified
              ? <Badge className="border bg-green-500/10 text-green-400 border-green-500/30 text-[10px]">Notified</Badge>
              : <Badge className="border bg-amber-500/15 text-amber-400 border-amber-500/40 text-[10px]">Not notified</Badge>
          ) : (
            <Badge className="border bg-secondary text-muted-foreground border-border text-[10px]">Audit</Badge>
          )}
        </div>
      ))}
    </div>
  );
}

/* ==================== PAGE ==================== */
export default function EventBoardPage() {
  const { dayId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState([]);           // open_shifts of this event, sorted by date
  const [shiftRows, setShiftRows] = useState([]); // per-employee db.shifts rows
  const [staffPool, setStaffPool] = useState([]);
  const [header, setHeader] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [showLog, setShowLog] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [actRes, arcRes] = await Promise.all([
        axios.get(`${API}/open-shifts`),
        axios.get(`${API}/open-shifts?archived=true`).catch(() => ({ data: [] })),
      ]);
      const all = [...(actRes.data || []), ...(arcRes.data || [])];
      const anchor = all.find((s) => s.id === dayId);
      if (!anchor) { toast.error('Event not found'); navigate('/admin/shifts/events'); return; }
      const key = `${normaliseKey(anchor.position)}|${normaliseKey(anchor.location)}`;
      const group = all
        .filter((s) => `${normaliseKey(s.position)}|${normaliseKey(s.location)}` === key)
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      setDays(group);
      setHeader(anchor);

      axios.get(`${API}/admin/event-board/log?day_ids=${group.map((g) => g.id).join(',')}`)
        .then((r) => setLogEntries(r.data?.entries || []))
        .catch(() => {});

      const dates = group.map((g) => g.date).filter(Boolean).sort();
      if (dates.length) {
        const cal = await axios.get(`${API}/shifts/calendar?start_date=${dates[0]}&end_date=${dates[dates.length - 1]}`);
        const rows = Array.isArray(cal.data) ? cal.data : (cal.data?.shifts || []);
        const ids = new Set(group.map((g) => g.id));
        setShiftRows(rows.filter((r) => ids.has(r.from_open_shift)));
      } else {
        setShiftRows([]);
      }
    } catch (e) {
      toast.error('Failed to load event board');
    } finally {
      setLoading(false);
    }
  }, [dayId, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/employees`);
        const list = Array.isArray(res.data) ? res.data : (res.data?.employees || []);
        setStaffPool(list.filter((u) => (u.status || 'active') === 'active'));
      } catch { /* staff search unavailable */ }
    })();
  }, []);

  const rowFor = (day, personId) =>
    shiftRows.find((r) => r.from_open_shift === day.id && r.employee_id === personId);

  const totals = useMemo(() => {
    const assigned = days.reduce((n, d) => n + ((d.claimed_by_list || []).length), 0);
    const cap = days.reduce((n, d) => n + (d.quantity || 1), 0);
    return { assigned, cap };
  }, [days]);

  const pendingLog = useMemo(
    () => logEntries.filter((e) => !e.notified && e.affected_user_id),
    [logEntries]
  );
  const affectedCount = useMemo(
    () => new Set(pendingLog.map((e) => e.affected_user_id)).size,
    [pendingLog]
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">Loading board…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="event-board-page">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button onClick={() => navigate('/admin/shifts/events')}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors mb-2"
              data-testid="board-back-btn">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Upcoming Shifts
            </button>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <LayoutGrid className="w-6 h-6 text-primary" strokeWidth={1.5} />
              {header?.position || 'EVENT BOARD'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> {header?.location}
              <span className="text-muted-foreground/50">·</span>
              {days.length} day{days.length === 1 ? '' : 's'}
              <span className="text-muted-foreground/50">·</span>
              {totals.assigned}/{totals.cap} staff
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge className="border bg-primary/10 text-primary border-primary/40 text-[11px]">
              Inline edits are silent — email only when you click Notify
            </Badge>
            <div className="flex items-center gap-2">
              <NotifyButton days={days} pendingCount={pendingLog.length} affectedCount={affectedCount} onDone={fetchAll} />
              <Button variant="outline" size="sm" onClick={() => setShowLog((v) => !v)}
                className="rounded-sm text-xs" data-testid="board-log-toggle">
                <History className="w-3.5 h-3.5 mr-1.5" />
                {showLog ? 'Hide change log' : `Change log (${logEntries.length})`}
              </Button>
            </div>
          </div>
        </div>

        {/* Change log panel */}
        {showLog && (
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="py-2.5 border-b border-border">
              <CardTitle className="text-xs font-barlow tracking-wider uppercase flex items-center gap-2">
                <History className="w-4 h-4 text-primary" /> Change Log — who changed what and when
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <LogPanel entries={logEntries} />
            </CardContent>
          </Card>
        )}

        {/* Day rows */}
        <div className="space-y-3">
          {days.map((day) => {
            const people = [...(day.claimed_by_list || [])].sort((a, b) => (a.user_name || '').localeCompare(b.user_name || ''));
            const dayRows = people.map((p) => rowFor(day, p.user_id)).filter(Boolean);
            return (
              <Card key={day.id} className="bg-card border-border rounded-sm" data-testid={`board-day-${day.date}`}>
                <CardHeader className="py-3 border-b border-border">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-3">
                      {fmtDate(day.date)}
                      <CapacityCell day={day} onSaved={fetchAll} />
                      {day.tbc && (
                        <Badge className="border bg-amber-500/15 text-amber-400 border-amber-500/40 text-[10px]" data-testid={`tbc-badge-${day.id}`}>
                          ⏳ TBC
                        </Badge>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <TbcToggle day={day} onSaved={fetchAll} />
                      <DayTimesButton day={day} rows={dayRows} onSaved={fetchAll} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="py-3 space-y-1.5">
                  {people.length === 0 && (
                    <p className="text-xs text-muted-foreground italic">No staff assigned yet</p>
                  )}
                  {people.map((p) => (
                    <div key={p.user_id}
                      className="flex flex-wrap items-center gap-3 py-1 px-2 rounded-sm hover:bg-secondary/50 transition-colors"
                      data-testid={`board-row-${day.id}-${p.user_id}`}>
                      <span className="text-sm text-foreground min-w-[160px]">{p.user_name}</span>
                      <TimeCell day={day} person={p} row={rowFor(day, p.user_id)} onSaved={fetchAll} />
                      <span className="flex-1" />
                      <ReplaceButton day={day} person={p} onSaved={fetchAll} />
                      <RemoveButton day={day} person={p} onSaved={fetchAll} />
                    </div>
                  ))}
                  <div className="pt-2 border-t border-border/50">
                    <AddStaffRow day={day} staffPool={staffPool} onSaved={fetchAll} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}
